import { cssVar, HStack, VStack, NavAppContent } from '@cad-arc';
import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { DrawerRenderer } from './drawers/components/drawer-renderer';
import { useEventPanelUpdates } from './event-panel-updates/use-event-panel-updates';
import { useIntegratedAssignedEvent } from './event-panel/event-state/use-integrated-assigned-event';
import { FrNavigation } from './fr-navigation/fr-navigation';
import { MessagingStoreDataProcessor } from './messaging/components/messaging-store-data-processor';
import { Modals } from './modals/components/modals';
import { FREsriMapWindowEnabler } from './map-window/components/fr-esri-map-window-enabler';
import { RouteManager } from './router/route-manager';
import { useAutoNavigateToTheEditUnitPage } from './fr-navigation/hooks/use-auto-navigate-to-the-edit-unit-page';
import { UnitEmergencyBanners } from './unit-emergency/unit-emergency-banner';
import { useEnabledFeatures } from './common/hooks/enabled-features/use-enabled-features';
import { useDrawerRender } from './drawers/components/drawer-context-provider';
import { useDexDeviceIdentificationErrorToast } from './data-exchange/use-dex-device-identification-error-toast';
import { useDexUserIdentificationErrorToast } from './data-exchange/use-dex-user-identification-error-toast';
import { FrEventsListTabProvider } from './fr-events-list-page/fr-events-list-context';
import { BannerContainer } from './common/components/banner-container';
import { DeprecationBanner } from './client-app-deprecation/deprecation-banner';
import { DeprecationBlockModal } from './client-app-deprecation/deprecation-block-modal';
import { FederatedSearchUnavailableBanner } from './federated-search/federated-search-unavailable-banner';
import { useIsFederatedSearchEnabledButNotAvailable } from './federated-search/federated-search-remote-state-context';
import { EsriMapConfigurationBanner } from './map-window/components/esri-map-configuration-banner';
import { MAP_WINDOW_PATH } from './router/constants';

/**
 * useIntegratedAssignedEvent contains state updates that will re-render the component that
 *  calls it. So, we wrap useIntegratedAssignedEvent in a component that is in a leaf node
 *  of the DOM tree so that the re-renders dont have any sub-components.
 */
function HoistedAssignedEventState() {
    useIntegratedAssignedEvent();
    return <></>;
}

export const FrApp = React.memo(function FrApp() {
    useEventPanelUpdates();
    useAutoNavigateToTheEditUnitPage();

    const enabledFeatures = useEnabledFeatures();
    const currentRouteIsMap = useLocation().pathname === MAP_WINDOW_PATH;
    const isFederatedSearchEnabledButNotAvailable = useIsFederatedSearchEnabledButNotAvailable();
    useDexDeviceIdentificationErrorToast({
        isToastEnabled: !isFederatedSearchEnabledButNotAvailable,
    });
    useDexUserIdentificationErrorToast({
        isToastEnabled: !isFederatedSearchEnabledButNotAvailable,
    });

    const drawerRender = useDrawerRender();
    const anyDrawerIsOpen = !!drawerRender?.key;

    const drawerBorderStyle = anyDrawerIsOpen
        ? `1px solid ${cssVar('arc.colors.selected.default')}`
        : 'none';

    return (
        <VStack height="100vh" gap={0} alignItems="normal">
            <DeprecationBlockModal />
            <RouteManager />
            <MessagingStoreDataProcessor />

            <FrEventsListTabProvider>
                <main
                    id="cad-content"
                    data-window-hash="new_queues"
                    style={{
                        backgroundColor: cssVar('arc.colors.surface.canvas'),
                        flexGrow: 100,
                        marginTop: 0,
                        height: '100%',
                        // Here to solve an issue with overflow in the event search window.
                        overflow: 'hidden',
                    }}
                >
                    <FrNavigation />
                    <NavAppContent>
                        <BannerContainer>
                            <DeprecationBanner />
                            <FederatedSearchUnavailableBanner />
                            {currentRouteIsMap && <EsriMapConfigurationBanner />}
                            {enabledFeatures.unitEmergencyEnabled && <UnitEmergencyBanners />}
                        </BannerContainer>
                        <HStack height="100%" ml={0}>
                            <div
                                id="inline-drawer-portal-id"
                                style={{
                                    height: '100%',
                                    position: 'relative',
                                    borderRight: drawerBorderStyle,
                                }}
                            />
                            <Outlet />
                            <FREsriMapWindowEnabler />
                        </HStack>
                    </NavAppContent>
                    <Modals />
                    <DrawerRenderer />
                    <HoistedAssignedEventState />
                </main>
            </FrEventsListTabProvider>
        </VStack>
    );
});
